import { useTranslation } from "react-i18next";
import { useController, useFormContext } from "react-hook-form";
import { Box, Typography, Card, CardActionArea, Radio, FormHelperText, RadioGroup } from "@mui/material";

import { BankAccountIcon, WalletIcon } from "../../../../../assets/icons/MUI/PaymentIcons";

import { paymentMethodSchema } from "../schema";
import { PaymentMethod } from "../types";

const PaymentMethodStepFields = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { field } = useController({
    name: "paymentMethod",
    control,
  });

  const handleOnMethodSelect = (method: PaymentMethod) => field.onChange(method);

  const paymentMethods = {
    wallet: {
      label: t("pages.payment.overview.v2.receivingDetailsPopUp.wallet"),
      icon: <WalletIcon />,
      description: t("pages.payment.overview.v2.receivingDetailsPopUp.walletDescription"),
    },
    bankAccount: {
      label: t("pages.payment.overview.v2.receivingDetailsPopUp.bankAccount"),
      icon: <BankAccountIcon />,
      description: t("pages.payment.overview.v2.receivingDetailsPopUp.bankAccountDescription"),
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", rowGap: "24px", padding: "0 24px" }}>
      <Typography
        component="h5"
        sx={{
          color: "var(--Gray-600, #475467)",
          textAlign: "center",
          fontFamily: "Montserrat",
          fontSize: "18px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "28px",
        }}
      >
        {t("pages.payment.overview.v2.receivingDetailsPopUp.selectPaymentMethodDescription")}
      </Typography>

      <RadioGroup sx={{ display: "flex", flexDirection: "row", gap: "24px" }}>
        {paymentMethodSchema.options.map((option) => {
          return (
            <Card
              key={option}
              sx={{
                flex: 1,
                border:
                  field.value === option
                    ? "1.5px solid var(--Green---100, #008B07)"
                    : "1.5px solid var(--Gray---100, #E7E7E7)",
                borderRadius: "12px",
                padding: "16px",
                position: "relative",
              }}
              onClick={() => handleOnMethodSelect(option)}
            >
              <Radio sx={{ position: "absolute", top: 0, right: 0 }} value={option} checked={field.value === option} />
              <CardActionArea
                sx={{
                  display: "flex",
                  flexDirection: " column",
                  gap: "16px",
                  alignItems: "flex-start",
                  "& .MuiCardActionArea-focusHighlight": {
                    backgroundColor: "transparent !important",
                  },
                }}
                disableRipple
              >
                {paymentMethods[option].icon}
                <Box sx={{ display: "flex", flexDirection: " column", gap: "8px" }}>
                  <Typography
                    sx={{
                      color: "var(--black-100, #1C1C1C)",
                      fontFamily: "Montserrat",
                      fontSize: "18px",
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: "28px",
                    }}
                  >
                    {paymentMethods[option].label}
                  </Typography>
                  <Typography
                    sx={{
                      color: "var(--Gray---400, #A0A0A0)",
                      fontFamily: "Montserrat",
                      fontSize: "13px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "normal",
                    }}
                  >
                    {paymentMethods[option].description}
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>
          );
        })}
      </RadioGroup>

      {errors.paymentMethod && (
        <FormHelperText error>
          {t(`pages.payment.overview.v2.receivingDetailsPopUp.${errors.paymentMethod.message}`)}
        </FormHelperText>
      )}
    </Box>
  );
};

export default PaymentMethodStepFields;
